import { useTranslation } from "react-i18next";

import { usePayRates } from "../../application/usePayRates";
import { EMPTY_RATES, PAY_RATE_TYPES, sizesSet, type PayRates } from "../../domain/PayRates";
import PayRatesForm from "../components/PayRatesForm";
import PractitionerReportView from "../components/PractitionerReportView";

const SELF = { kind: "self" } as const;

/**
 * The practitioner's own reports, and the rates their amounts come from.
 *
 * Every therapist and mentor has this page, in a centre or on their own. A
 * centre's rates are shown for what they are — the centre's decision, read
 * only here — beside the rates the practitioner sets for their own practice.
 */
export default function PractitionerReportsPage() {
    const { t } = useTranslation();

    return (
        <div className="space-y-6">
            <PractitionerReportView scope={SELF} title={t("reports.title")} subtitle={t("reports.subtitle")} />
            <RatesSection />
        </div>
    );
}

function RatesSection() {
    const { t, i18n } = useTranslation();
    const { rates, loading, error, save, saving } = usePayRates();

    if (loading || error || !rates) return null;

    const money = (amount: string | null) =>
        amount === null
            ? t("reports.rates.notSet")
            : new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(
                  Number(amount)
              );

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {rates.center && (
                <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                    <h2 className="text-base font-semibold text-content-primary">
                        {t("reports.rates.centerTitle", { center: rates.center.center_name ?? "" })}
                    </h2>
                    <p className="mb-4 mt-1 text-sm text-content-secondary">{t("reports.rates.centerHint")}</p>
                    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {PAY_RATE_TYPES.map((type) => (
                            <div key={type} className="rounded-xl bg-surface-sunken p-3">
                                <dt className="text-xs font-medium text-content-tertiary">
                                    {t(`reports.rates.${type}`)}
                                </dt>
                                <dd className="mt-1 text-sm font-semibold tabular-nums text-content-primary">
                                    {money(rates.center!.effective[type])}
                                </dd>
                            </div>
                        ))}
                    </dl>
                    {sizesSet(rates.center.effective) > 0 && (
                        <div className="mt-4">
                            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-content-tertiary">
                                {t("reports.rates.sizes.title")}
                            </h3>
                            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                                {Object.entries(rates.center.effective.group_sizes)
                                    .filter(([, amount]) => amount)
                                    .sort(([a], [b]) => Number(a) - Number(b))
                                    .map(([size, amount]) => (
                                        <div key={size} className="rounded-xl bg-surface-sunken px-3 py-2">
                                            <dt className="text-xs font-medium text-content-tertiary">
                                                {t("reports.rates.sizes.label", { count: Number(size) })}
                                            </dt>
                                            <dd className="mt-0.5 text-sm font-semibold tabular-nums text-content-primary">
                                                {money(amount)}
                                            </dd>
                                        </div>
                                    ))}
                            </dl>
                        </div>
                    )}
                </section>
            )}
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <h2 className="text-base font-semibold text-content-primary">{t("reports.rates.ownTitle")}</h2>
                <p className="mb-4 mt-1 text-sm text-content-secondary">{t("reports.rates.ownHint")}</p>
                <PayRatesForm
                    idPrefix="own-rates"
                    value={rates.own ?? EMPTY_RATES}
                    onSave={(value: PayRates) => save(value)}
                    saving={saving}
                />
            </section>
        </div>
    );
}
